import { ExpandLess, ExpandMore, Tune as Settings2Icon } from '@mui/icons-material';
import { Box, Button, ButtonBase, Checkbox, Collapse, Divider, Popover, Stack, Typography } from '@mui/material';
import { useState, MouseEvent } from 'react';
import ReactGA from 'react-ga4';
import { OrderAttrFilter } from 'src/components/dispatch/orderAttrFilter';
import OrderAttrFilterSection from 'src/components/dispatch/OrderAttrFilterSection';
import {
  buildDisplayMetricGroups,
  DisplayMetric,
  DEFAULT_VEHICLE_METRICS,
  checkedDisplayMetrics,
  isPlaceMetric,
  resolvePlaceMetrics,
  isVehicleMetric,
  isValueMetric,
  VALUE_METRICS,
  VEHICLE_METRICS,
  visibleDisplayMetrics,
} from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';

// 既存 14 ファイルがこのモジュールから型を import しているため re-export で互換を保つ。
export type { ValueMetric, VehicleMetric, InfoMetric, DisplayMetric } from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';

const sectionLabelSx = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary',
} as const;

/**
 * セクション見出し。note はその設定がどのリストに効くかの注記。
 * 数値指標だけが車両リストにも効くため、注記が無いと表示情報との違いが伝わらない。
 */
const sectionHeader = (label: string, note?: string) => (
  <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
    <Typography variant="caption" sx={sectionLabelSx} display="block">{label}</Typography>
    {note && (
      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }} display="block">
        {note}
      </Typography>
    )}
  </Box>
);

interface Props {
  value: DisplayMetric[];
  onChange: (metrics: DisplayMetric[]) => void;
  /** license.custom_input_fields。license 未取得のあいだは空配列として扱う */
  customInputFieldNames: string[];
  isLicenseReady: boolean;
  /** GA の screen_name。/map と convenience で異なるため呼び出し側から渡す */
  screenName: string;
  /** 表示対象（配送形態・区間種別）の絞り込み。非表示にしている値を持つ */
  orderAttrFilter: OrderAttrFilter;
  onOrderAttrFilterChange: (next: OrderAttrFilter) => void;
}

const DisplayMetricToggle = ({
  value, onChange, customInputFieldNames, isLicenseReady, screenName, orderAttrFilter, onOrderAttrFilterChange,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  // チェック状態は実効集合（checkedDisplayMetrics）で判定する。数値指標が保存値に
  // 1 つも無いとき、VehicleInfoView は quantity にフォールバックして容量を描くため、
  // チェック状態もそれに合わせて quantity を有効として扱わないと表示と食い違う。
  // 積地・降地も同じ理由で、両方外れているときは既定表示の降地を有効として扱う。
  const valueSet = new Set(checkedDisplayMetrics(value));

  // license 未取得のあいだは任意項目を「無い」と確定させない（描かないだけ）。
  const availableCustomNames = isLicenseReady ? customInputFieldNames : [];
  const groups = buildDisplayMetricGroups(availableCustomNames);
  // バッジと全解除防止は「実効集合 かつ 可視集合」基準。保存値には他社の custom: キーが
  // 残る一方、数値指標ゼロの保存値では quantity が実効的に有効になる。チェック状態
  // (valueSet、上記) と同じ effectiveDisplayMetrics を通すことでバッジ件数とチェック数を
  // 一致させる（通さないと保存値 ['grossProfit'] のときチェックは2件なのにバッジが1件になる）。
  const activeCount = visibleDisplayMetrics(checkedDisplayMetrics(value), availableCustomNames).length;

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => ({
    location: true,
    order: false,
    custom: false,
  }));

  const toggle = (metric: DisplayMetric, groupLabel: '数値指標 切替' | '車両リスト 切替' | '表示情報 切替') => {
    const nextChecked = !valueSet.has(metric);
    ReactGA.event('change', {
      screen_name: screenName,
      button_name: groupLabel,
      label: `${metric} ${nextChecked ? 'ON' : 'OFF'}`,
    });
    // 編集は生の保存値へ（他社の custom: キーを保持する）。
    // ただし車両タグは未保存のとき既定で ON になっているため、生の保存値から filter
    // しても消えない。そのタグを押すときだけ、既定を保存値に確定させてから外す。
    const withVehicleDefaults = isVehicleMetric(metric) && !value.some(isVehicleMetric)
      ? [...value, ...DEFAULT_VEHICLE_METRICS]
      : value;
    // 積地・降地も同じ扱い。両方外れているときは降地が既定表示でチェックが付いて
    // 見えるので、確定させずに積地を足すと降地まで消える（実機で確認）。
    const places = resolvePlaceMetrics(value);
    const base = isPlaceMetric(metric) && places.destination && !value.includes('destination')
      ? [...withVehicleDefaults, 'destination' as DisplayMetric]
      : withVehicleDefaults;
    const nextRaw = nextChecked ? [...base, metric] : base.filter((m) => m !== metric);
    // 判定は実効集合へ（チェック状態と同じ effectiveDisplayMetrics を通す）。生の
    // nextRaw だけで判定すると、保存値が数値指標ゼロのレガシー値 (['grossProfit'] 等)
    // のときチェック表示は quantity を含めて 2 件に見えるのに、生の可視件数だけで
    // 判定すると nextRaw=[] が可視 0 件と誤判定されて粗利も外せない行き止まりになる
    // (実測で発生した不具合)。
    if (visibleDisplayMetrics(checkedDisplayMetrics(nextRaw), availableCustomNames).length === 0) return;
    // 数値指標 (quantity/weight/volume) は契約上常に最低 1 つ必要（車両カードが常に
    // 容量とゲージを描画するため）。OFF にした結果ゼロになるなら弾く。粗利や表示情報
    // キーの OFF はこの契約の対象外 (isValueMetric で数値指標だけに絞る)。
    if (isValueMetric(metric) && !nextChecked && !nextRaw.some(isValueMetric)) return;
    onChange(nextRaw);
  };

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
    ReactGA.event('click', { screen_name: screenName, button_name: '表示項目' });
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const checkboxRow = (key: DisplayMetric, label: string, groupLabel: '数値指標 切替' | '車両リスト 切替' | '表示情報 切替') => (
    <Stack
      key={key}
      direction="row"
      alignItems="center"
      spacing={1}
      component="label"
      sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Checkbox
        size="small"
        checked={valueSet.has(key)}
        onChange={() => toggle(key, groupLabel)}
        inputProps={{ 'aria-label': label }}
        sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 } }}
      />
      <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary' }}>
        {label}
      </Typography>
    </Stack>
  );

  return (
    <>
      <Button
        size="small"
        onClick={handleOpen}
        startIcon={<Settings2Icon sx={{ fontSize: 14 }} />}
        sx={{
          height: 28,
          minWidth: 0,
          px: 1,
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'none',
          color: 'text.secondary',
          gap: 0.5,
          '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
        }}
      >
        表示項目
        {activeCount > 0 && (
          <Box
            component="span"
            sx={{
              ml: 0.25,
              px: 0.75,
              py: 0.25,
              borderRadius: 999,
              bgcolor: 'hsl(var(--primary) / 0.15)',
              color: 'primary.main',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {activeCount}
          </Box>
        )}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 208, p: 0 } } }}
      >
        {sectionHeader('数値指標', '車両リスト・未割当リストに反映')}
        <Stack sx={{ px: 1, pb: 1 }} spacing={0.25}>
          {VALUE_METRICS.map(({ key, label }) => checkboxRow(key, label, '数値指標 切替'))}
        </Stack>

        <Divider />

        {/* 3 カテゴリを束ねる見出し。どこに効く設定なのかが分からないという指摘への対応 */}
        {sectionHeader('表示情報', '未割当リストに反映')}

        {/*
          カテゴリヘッダは ButtonBase（= ネイティブ button）。onClick だけの Stack だと
          キーボードで開閉できない。内側に別の操作要素を置かないので、LaneCard のような
          イベント境界の工夫は不要。
        */}
        {groups.map((group) => {
          const activeInGroup = group.items.filter((i) => valueSet.has(i.key)).length;
          return (
            <Box key={group.id}>
              <ButtonBase
                onClick={() => setOpenGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
                aria-expanded={openGroups[group.id]}
                sx={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1.5, py: 0.75, textAlign: 'left', '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}
                  >
                    {group.label}
                  </Typography>
                  {activeInGroup > 0 && (
                    <Box
                      sx={{
                        px: 0.5, py: 0.25, borderRadius: 999, bgcolor: 'hsl(var(--primary) / 0.15)',
                        color: 'primary.main', fontSize: 9, fontWeight: 700, lineHeight: 1,
                      }}
                    >
                      {activeInGroup}
                    </Box>
                  )}
                </Stack>
                {openGroups[group.id] ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
              </ButtonBase>
              <Collapse in={openGroups[group.id]}>
                <Stack sx={{ px: 1, pb: 1 }} spacing={0.25}>
                  {group.items.map(({ key, label }) => checkboxRow(key, label, '表示情報 切替'))}
                </Stack>
              </Collapse>
            </Box>
          );
        })}

        <Divider />

        {/* 案件と車両の絞り込み。表示情報（見せ方）とは効き方が違うので独立したセクションにする。 */}
        <OrderAttrFilterSection
          value={orderAttrFilter}
          onChange={(next, changed) => {
            ReactGA.event('change', {
              screen_name: screenName,
              button_name: '表示対象 切替',
              label: `${changed.label} ${changed.visible ? 'ON' : 'OFF'}`,
            });
            onOrderAttrFilterChange(next);
          }}
        />

        <Divider />

        {/* 車両リスト専用の指標。カード側には出ないので表示情報のアコーディオンとは分ける。 */}
        {sectionHeader('車両リスト表示タグ')}
        <Stack sx={{ px: 1, pb: 1 }} spacing={0.25}>
          {VEHICLE_METRICS.map(({ key, label }) => checkboxRow(key, label, '車両リスト 切替'))}
        </Stack>
      </Popover>
    </>
  );
};

export default DisplayMetricToggle;
